import api from './axiosInstance';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Download a blob from the API using native fetch.
 * Avoids axios interceptor issues with blob + params combinations.
 */
const fetchBlob = async (path, params = {}) => {
  const token = localStorage.getItem('epc_token');

  // Build query string manually — no reliance on axios params serialization
  const qs = Object.keys(params).length > 0
    ? '?' + new URLSearchParams(params).toString()
    : '';

  const res = await fetch(`${BASE_URL}${path}${qs}`, {
    method: 'GET',
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  });

  if (!res.ok) {
    let msg = `Download failed (${res.status})`;
    try { const j = await res.json(); msg = j.message || j.error || msg; } catch { /* ignore */ }
    throw new Error(msg);
  }

  return res.blob();
};

const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export const downloadTemplate = async () => {
  const blob = await fetchBlob('/accessory-excel/template');
  triggerDownload(blob, 'accessory_template.xlsx');
};

export const downloadPrices = async (filter = {}) => {
  const params = {};
  if (filter.departmentId) params.departmentId = filter.departmentId;
  if (filter.groupCode)    params.groupCode    = filter.groupCode;
  const blob = await fetchBlob('/accessory-excel/download', params);
  let suffix = '';
  if (filter.groupCode)           suffix = `_${filter.groupCode}`;
  else if (filter.departmentName) suffix = `_${filter.departmentName.replace(/\s+/g, '_')}`;
  triggerDownload(blob, `accessory_prices${suffix}.xlsx`);
};

export const uploadPrices = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/accessory-excel/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const pollJob = (jobId) => api.get(`/accessory-excel/jobs/${jobId}`);

/**
 * Download material-accessory mapping.
 * @param {number[]} [materialIds] - If provided, only those materials are included.
 * @param {Array<{materialId, typeOfInstallationId}>} [combinations] - If provided, only those Material+Type combinations are included.
 */
export const downloadAccessoryMasterData = async (filter = {}) => {
  const params = {};
  if (filter.departmentId) params.departmentId = filter.departmentId;
  if (filter.groupCode)    params.groupCode    = filter.groupCode;
  const blob = await fetchBlob('/accessory-excel/master-data', params);
  let suffix = '';
  if (filter.groupCode)           suffix = `_${filter.groupCode}`;
  else if (filter.departmentName) suffix = `_${filter.departmentName.replace(/\s+/g, '_')}`;
  triggerDownload(blob, `accessory_master_data${suffix}.xlsx`);
};

export const downloadMapping = async (materialIds, combinations) => {
  const params = {};

  // If combinations provided, use those (more specific)
  if (Array.isArray(combinations) && combinations.length > 0) {
    // Encode as "matId|typeId,matId|typeId,..."
    params.combinations = combinations.map((c) => `${c.materialId}|${c.typeOfInstallationId}`).join(',');
  } else if (Array.isArray(materialIds) && materialIds.length > 0) {
    // Otherwise use material IDs if provided
    params.materialIds = materialIds.join(',');
  }

  const blob = await fetchBlob('/accessory-excel/mapping', params);

  let fileName = 'material_accessories_mapping.xlsx';
  if (params.combinations) fileName = 'material_accessories_mapping_selected.xlsx';
  else if (params.materialIds) fileName = 'material_accessories_mapping_filtered.xlsx';

  triggerDownload(blob, fileName);
};
